// Comandos do agregado Preço — a Tabela de Preços é a casa única do preço
// (ADR-0006): preço base e preço de Cliente com vigências, correção/remoção de
// vigência (dos dois), importação em lote. Dado puro; o executor só chama o domínio.
//
// Datas: `aplicarTabela` sem `desde` usa o `ctx.hoje` do servidor (a grade da
// tela não tem campo de data). A importação CSV manda a data escolhida.

import type { DataISO } from "../domain/datas.ts";
import type { Snapshot } from "../domain/snapshot.ts";
import {
  corrigirVigenciaPrecoBase,
  corrigirVigenciaPrecoCliente,
  definirPrecoBase,
  definirPrecoCliente,
  limparPrecoCliente,
  limparTodosOsPrecos,
  removerPrecoBaseVigencia,
  removerPrecoClienteVigencia,
} from "../domain/precificacao.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import {
  comoObjeto,
  exigirData,
  exigirTexto,
  lista,
  paraNumero,
  umDe,
} from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

export type ItemPrecoComando =
  | { escopo: "base"; tipoId: string; centavos: number }
  | {
      escopo: "cliente";
      clienteId: string;
      tipoId: string;
      /** `null` = limpar o preço próprio do Cliente (volta ao base). */
      centavos: number | null;
    };

// Os campos de data são `DataISO`, que é o que `exigirData` devolve — sem `as`
// no executor (ADR-0013).
export type ComandoPreco =
  | { tipo: "aplicarTabela"; desde?: DataISO; itens: ItemPrecoComando[] }
  | {
      tipo: "corrigirVigenciaBase";
      tipoId: string;
      de: DataISO;
      para: DataISO;
      centavos: number;
    }
  | { tipo: "removerVigenciaBase"; tipoId: string; desde: DataISO }
  | {
      tipo: "corrigirVigenciaCliente";
      clienteId: string;
      tipoId: string;
      de: DataISO;
      para: DataISO;
      centavos: number;
    }
  | { tipo: "removerVigenciaCliente"; clienteId: string; tipoId: string; desde: DataISO }
  | { tipo: "limparTudo" };

export function executarComandoPreco(
  snapshot: Snapshot,
  cmd: ComandoPreco,
  ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "aplicarTabela": {
      const desde = cmd.desde ?? ctx.hoje;
      let out = snapshot;
      for (const item of cmd.itens) {
        if (item.escopo === "base") {
          out = definirPrecoBase(out, item.tipoId, item.centavos, desde);
        } else if (item.centavos == null) {
          out = limparPrecoCliente(out, item.clienteId, item.tipoId);
        } else {
          out = definirPrecoCliente(
            out,
            item.clienteId,
            item.tipoId,
            item.centavos,
            desde,
          );
        }
      }
      return out;
    }
    case "corrigirVigenciaBase":
      return corrigirVigenciaPrecoBase(snapshot, cmd.tipoId, cmd.de, cmd.para, cmd.centavos);
    case "removerVigenciaBase":
      return removerPrecoBaseVigencia(snapshot, cmd.tipoId, cmd.desde);
    case "corrigirVigenciaCliente":
      return corrigirVigenciaPrecoCliente(
        snapshot,
        cmd.clienteId,
        cmd.tipoId,
        cmd.de,
        cmd.para,
        cmd.centavos,
      );
    case "removerVigenciaCliente":
      return removerPrecoClienteVigencia(snapshot, cmd.clienteId, cmd.tipoId, cmd.desde);
    case "limparTudo":
      return limparTodosOsPrecos(snapshot);
    default:
      return assertNunca(cmd);
  }
}

function validarItem(valor: unknown, i: number): ItemPrecoComando {
  const o = comoObjeto(valor, `Item de preço #${i + 1}`);
  const escopo = umDe(o.escopo, `itens[${i}].escopo`, ["base", "cliente"]);
  if (escopo === "base") {
    return {
      escopo: "base",
      tipoId: exigirTexto(o.tipoId, `itens[${i}].tipoId`),
      centavos: paraNumero(o.centavos),
    };
  }
  return {
    escopo: "cliente",
    clienteId: exigirTexto(o.clienteId, `itens[${i}].clienteId`),
    tipoId: exigirTexto(o.tipoId, `itens[${i}].tipoId`),
    centavos: o.centavos == null ? null : paraNumero(o.centavos),
  };
}

export function validarComandoPreco(valor: unknown): ComandoPreco {
  const c = comoObjeto(valor, "Comando de preço");
  switch (c.tipo) {
    case "aplicarTabela": {
      const cmd: ComandoPreco = {
        tipo: "aplicarTabela",
        itens: lista(c.itens, "itens", validarItem),
      };
      // `desde` só entra se veio como data válida; ausente/"" → ctx.hoje.
      if (c.desde !== undefined && c.desde !== "") {
        cmd.desde = exigirData(c.desde, "desde");
      }
      return cmd;
    }
    case "corrigirVigenciaBase":
      return {
        tipo: "corrigirVigenciaBase",
        tipoId: exigirTexto(c.tipoId, "tipoId"),
        de: exigirData(c.de, "de"),
        para: exigirData(c.para, "para"),
        centavos: paraNumero(c.centavos),
      };
    case "removerVigenciaBase":
      return {
        tipo: "removerVigenciaBase",
        tipoId: exigirTexto(c.tipoId, "tipoId"),
        desde: exigirData(c.desde, "desde"),
      };
    case "corrigirVigenciaCliente":
      return {
        tipo: "corrigirVigenciaCliente",
        clienteId: exigirTexto(c.clienteId, "clienteId"),
        tipoId: exigirTexto(c.tipoId, "tipoId"),
        de: exigirData(c.de, "de"),
        para: exigirData(c.para, "para"),
        centavos: paraNumero(c.centavos),
      };
    case "removerVigenciaCliente":
      return {
        tipo: "removerVigenciaCliente",
        clienteId: exigirTexto(c.clienteId, "clienteId"),
        tipoId: exigirTexto(c.tipoId, "tipoId"),
        desde: exigirData(c.desde, "desde"),
      };
    case "limparTudo":
      return { tipo: "limparTudo" };
    default:
      throw new Error(`Tipo de comando de preço desconhecido: ${String(c.tipo)}.`);
  }
}
